import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb, Row, Col, Space } from 'antd';
import { DeliveredProcedureOutlined } from '@ant-design/icons';
import { JobDetailed } from '../../types/Job';
import { Client } from '../../types/Client';
import { JOB_MAP_OPERATION } from '../../mappers/job.mapper';
import JobController from '../../controllers/job.controller';
import ClientController from '../../controllers/client.controller';
import { JobDetailCard, JobDeliveryTimeline } from '../../components/Job';
import ClientDetailCard from '../../components/Client/ClientDetailCard';
import { NoRecordFound } from '../../components/Shared';

const JobDetailContainer: React.FC = (): React.ReactElement => {
    const params: { jobId: string } = useParams();
    const [jobData, setJobData] = useState<JobDetailed | undefined>();
    const [clientData, setClientData] = useState<Client | undefined>();

    const getJobById = async (): Promise<void> => {
        try {
            const job = (await JobController.getJobById(params.jobId, JOB_MAP_OPERATION.DETAILED)) as JobDetailed;
            setJobData(job);
        } catch (error) {
            setJobData(undefined);
        }
    };

    const getClientById = async (clientId: number): Promise<void> => {
        try {
            const client = await ClientController.getClientById(clientId);
            setClientData(client);
        } catch (error) {
            setClientData(undefined);
        }
    };

    useEffect(() => {
        if (jobData?.client_id) getClientById(jobData.client_id);
    }, [jobData]);

    useEffect(() => {
        getJobById();
    }, [params.jobId]);

    return (
        <>
            <Breadcrumb className="mb-3">
                <Breadcrumb.Item>
                    <DeliveredProcedureOutlined className="d-inline-flex mr-1" />
                    <Link to="/admin/jobs" title="Jobs">
                        Jobs
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Job details</Breadcrumb.Item>
            </Breadcrumb>
            <Row gutter={[24, 24]}>
                {jobData ? (
                    <>
                        <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                            <Space direction="vertical" size="large" className="w-100">
                                <JobDetailCard
                                    data={{
                                        id: jobData.id,
                                        apiJobId: jobData.apiJobId,
                                        createdAt: jobData.createdAt,
                                        assignmentCode: jobData.assignmentCode,
                                        status: jobData.status,
                                        pickupAt: jobData.pickupAt,
                                        packageType: jobData.packageType,
                                        transportType: jobData.transportType,
                                        comment: jobData.comment,
                                        dashboardURL: jobData.dashboardURL,
                                        stackId: jobData.stackId,
                                    }}
                                />
                                {clientData && (
                                    <ClientDetailCard
                                        data={{
                                            id: clientData.id,
                                            name: clientData.name,
                                            stuartClientId: clientData.stuartClientId,
                                            storeId: clientData.storeId || '-',
                                            status: clientData.status,
                                        }}
                                    />
                                )}
                            </Space>
                        </Col>
                        <Col span={16} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 16 }}>
                            <JobDeliveryTimeline deliveries={jobData.deliveries} />
                        </Col>
                    </>
                ) : (
                    <Col span={24} className="shadow-sm">
                        <NoRecordFound message="No job found." />
                    </Col>
                )}
            </Row>
        </>
    );
};

export default JobDetailContainer;
